import { setupDev } from '@tamagui/core';
import { type Href, router, usePathname } from 'expo-router';
import React, { type ReactNode, useCallback } from 'react';
import { Button as TamaguiButton, ScrollView, XStack, YStack } from 'tamagui';

import {
    BackButton,
    BackButtonIconType,
} from '../components/buttons/BackButton';
import { SafeAreaView } from '../components/containers/Containers';
import {
    BodyMediumRegular,
    H6Emphasized,
    H6Regular,
} from '../components/text/Typography';
import { TooltipGroupProvider } from '../components/tooltip/Tooltip';
import { themes } from '../themes/SmoothieThemes';
import {
    BlenderDirectory,
    type BlenderDirectoryCategory,
    type LightOrDarkTheme,
} from './BlenderDirectory';
import {
    TOOLTIP_GROUP_1_NAME,
    TOOLTIP_GROUP_2_NAME,
} from './components/Tooltips';

setupDev({
    visualizer: {
        key: 'Alt',
        delay: 800,
    },
});

export const BlenderLayout = ({
    theme,
    onToggleTheme,
    children,
    currentPath,
    componentGroups: groups = BlenderDirectory,
}: {
    theme: LightOrDarkTheme;
    onToggleTheme: (theme: LightOrDarkTheme) => void;
    children: ReactNode;
    currentPath?: string;
    componentGroups?: BlenderDirectoryCategory[];
}) => {
    // Use callbacks if provided, otherwise try expo-router
    const pathname = currentPath ?? usePathname();

    const navigate = useCallback((componentName: string) => {
        router.navigate({
            pathname: '/dev/design-system/[component]',
            params: {
                component: componentName,
            },
        } as unknown as Href);
    }, []);

    return (
        <SafeAreaView
            style={{ flex: 1, backgroundColor: themes[theme].background }}
        >
            {/* Top Navigation */}
            <XStack
                justifyContent="space-between"
                alignItems="center"
                padding="$2"
                marginBottom="$4"
                $sm={{ padding: '$4', marginBottom: 0 }}
            >
                <YStack width={65}>
                    <BackButton iconType={BackButtonIconType.ARROW_LEFT} />
                </YStack>
                <H6Emphasized>🍹 Smoothie Blender 🍹</H6Emphasized>
                <YStack width={65}>
                    <ThemeToggleButton
                        theme={theme}
                        onToggleTheme={onToggleTheme}
                    />
                </YStack>
            </XStack>

            <XStack flex={1}>
                {/* Left Sidebar Navigation */}
                <YStack
                    width={250}
                    borderRightWidth={1}
                    borderRightColor="$gray200"
                    backgroundColor={themes[theme].background}
                    paddingLeft="$3"
                    display="none"
                    $sm={{ display: 'flex' }}
                >
                    <ScrollView automaticallyAdjustKeyboardInsets={true}>
                        <YStack
                            gap="$4"
                            paddingHorizontal="$4"
                            paddingBottom={200}
                        >
                            {groups.map((group) => (
                                <SidebarGroup
                                    key={group.category}
                                    group={group}
                                    pathname={pathname}
                                    navigate={navigate}
                                />
                            ))}
                        </YStack>
                    </ScrollView>
                </YStack>

                {/* Main Content Area */}
                <YStack flex={1}>
                    <ScrollView backgroundColor={themes[theme].background}>
                        <YStack
                            flex={1}
                            gap="$8"
                            paddingBottom={50}
                            paddingHorizontal="$4"
                            $sm={{ paddingHorizontal: '$8' }}
                        >
                            {children}
                        </YStack>
                    </ScrollView>
                </YStack>
            </XStack>

            <TooltipGroupProvider tooltipGroupName={TOOLTIP_GROUP_1_NAME} />
            <TooltipGroupProvider tooltipGroupName={TOOLTIP_GROUP_2_NAME} />
        </SafeAreaView>
    );
};

const SidebarGroup = ({
    group,
    pathname,
    navigate,
}: {
    group: BlenderDirectoryCategory;
    pathname: string;
    navigate: (route: string) => void;
}) => {
    return (
        <YStack key={group.category} gap="$2">
            <BodyMediumRegular fontWeight="600" color="$gray1100">
                {group.category}
            </BodyMediumRegular>
            <YStack gap="$1" paddingLeft="$4">
                {group.entries.map((component) => {
                    if (!component) {
                        return null;
                    }
                    const isActive = pathname.endsWith(component.name);
                    return (
                        <TamaguiButton
                            key={component.name}
                            unstyled
                            onPress={() => {
                                navigate(component.name);
                            }}
                            cursor="pointer"
                            paddingVertical="$2"
                            paddingHorizontal="$2"
                            borderRadius="$2"
                            backgroundColor={
                                isActive ? '$gray200' : 'transparent'
                            }
                            hoverStyle={{
                                backgroundColor: '$gray200',
                            }}
                        >
                            <BodyMediumRegular
                                color={isActive ? '$black' : '$gray1000'}
                                fontWeight={isActive ? '600' : '400'}
                            >
                                {component.title}
                            </BodyMediumRegular>
                        </TamaguiButton>
                    );
                })}
            </YStack>
        </YStack>
    );
};

const ThemeToggleButton = ({
    theme,
    onToggleTheme,
}: {
    theme: LightOrDarkTheme;
    onToggleTheme: (theme: LightOrDarkTheme) => void;
}) => {
    return (
        <TamaguiButton
            borderRadius="$5"
            padding="$1"
            onPress={() => {
                onToggleTheme(theme === 'dark' ? 'light' : 'dark');
            }}
        >
            <H6Regular>🌞 🌚</H6Regular>
        </TamaguiButton>
    );
};
